import React from 'react';

interface CompletionDotProps {
  completed: boolean;
  onClick?: () => void;
  size?: 'sm' | 'md' | 'lg';
  interactive?: boolean;
  title?: string;
}

/**
 * CompletionDot
 * 
 * A subtle completion indicator that replaces checkbox UI.
 * - Filled green dot = completed
 * - Empty gray dot = not completed
 * - Optional click handler for actions
 */
const CompletionDot: React.FC<CompletionDotProps> = ({
  completed,
  onClick,
  size = 'md',
  interactive = false,
  title,
}) => {
  const sizeClasses = {
    sm: 'w-2 h-2',
    md: 'w-3 h-3',
    lg: 'w-4 h-4',
  };

  const dotTitle = title ?? (completed ? 'Afgerond' : 'Nog open');

  const baseClasses = `
    ${sizeClasses[size]}
    rounded-full
    transition-all duration-200
    flex-shrink-0
  `;

  const stateClasses = completed
    ? 'bg-emerald-500 border-2 border-emerald-300'
    : 'bg-transparent border-2 border-gray-300';

  const interactiveClasses = interactive
    ? 'cursor-pointer hover:scale-110 hover:border-emerald-400'
    : '';

  if (interactive && onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={`${baseClasses} ${stateClasses} ${interactiveClasses}`}
        title={dotTitle}
        aria-label={dotTitle}
      />
    );
  }

  return (
    <span
      className={`${baseClasses} ${stateClasses}`}
      title={dotTitle}
      aria-label={dotTitle}
    />
  );
};

export default CompletionDot;
